import type { JSX } from 'react';
import { styled } from '@mui/material';
import type { ITimelineMarker } from '@/types/PatientDashboard/PatientTimeline.types';
import { MarkerDot, MarkerStackDot } from './MarkerDotCluster.styles';
import SampleMark from './SampleMark';

const MarkerDotStack = styled('span')({
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  flexShrink: 0,
  transition: 'transform 150ms ease',
});

const MAX_STACK_DOTS = 3;

interface IProps {
  marker: ITimelineMarker;
}

export default function MarkerDotCluster({ marker }: IProps): JSX.Element {
  const stackIds = (marker.stackItemIds ?? []).slice(0, MAX_STACK_DOTS);
  if (stackIds.length > 1) {
    return (
      <MarkerDotStack
        className="timeline-marker-dot-stack"
        data-marker-date={marker.date}
        data-marker-id={marker.id}
      >
        {stackIds.map((id, index) => (
          <MarkerStackDot
            key={id}
            className="timeline-marker-dot"
            style={{ background: marker.colour, marginTop: index === 0 ? 0 : undefined }}
          />
        ))}
      </MarkerDotStack>
    );
  }

  const isLiquidBiopsy = marker.sampleTrackId === 'lb';
  const samples = marker.groupedSamples ?? [];

  if (samples.length > 1) {
    const visibleSamples = samples.slice(0, MAX_STACK_DOTS);
    // Prefer the sample explicitly flagged as parent; fall back to the first
    // dot if none is flagged, so groups with unknown/missing lineage still
    // render a single teardrop rather than none.
    const parentIndex = visibleSamples.findIndex((sample) => sample.isParent);
    return (
      <MarkerDotStack
        className="timeline-marker-dot-stack"
        data-marker-date={marker.date}
        data-marker-id={marker.id}
      >
        {visibleSamples.map((sample, index) => (
          <SampleMark
            key={`${marker.id}-${sample.id}`}
            colour={marker.colour}
            isLiquidBiopsy={isLiquidBiopsy}
            isParent={parentIndex === -1 ? index === 0 : index === parentIndex}
            index={index}
          />
        ))}
      </MarkerDotStack>
    );
  }

  // A lone Liquid Biopsy sample (parent or child) is a single teardrop; any other
  // row is a single plain dot.
  if (isLiquidBiopsy) {
    return (
      <MarkerDotStack
        className="timeline-marker-dot-stack"
        data-marker-date={marker.date}
        data-marker-id={marker.id}
      >
        <SampleMark colour={marker.colour} isLiquidBiopsy isParent index={0} />
      </MarkerDotStack>
    );
  }

  return (
    <MarkerDot
      className="timeline-marker-dot timeline-marker-dot-single"
      data-marker-date={marker.date}
      data-marker-id={marker.id}
      style={{ background: marker.colour }}
    />
  );
}
